import { Box, Grid, IconButton, Menu, MenuItem, useTheme } from '@mui/material'
import { FormTextField } from '../../common/form-fields-wrappers';
import { DeleteRounded, UploadRounded, VisibilityRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { IVehicleStatusPhotosForm } from '../../models/models';
import { useState } from 'react';
import { TFileRefs } from './AddPhotos';
import { GridMoreVertIcon } from '@mui/x-data-grid';

type PhotoFileFieldProps = {
    name: keyof IVehicleStatusPhotosForm,
    fileRefs: TFileRefs,
    required: boolean,
}

function openBase64InNewTab(data: ArrayBuffer, mimeType: string) {
    const byteArray = new Uint8Array(data);
    const file = new Blob([byteArray], { type: mimeType + ";base64" });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, "_blank");
}

function PhotoFileField(props: PhotoFileFieldProps) {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const fileRef = props.fileRefs[props.name];

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

    const formContext = useFormContext<IVehicleStatusPhotosForm>();
    const [hasFile, setHasFile] = useState<boolean>(!!formContext.formState.dirtyFields[props.name]);

    const error = formContext.formState.errors[props.name];

    if (hasFile && !!error) {
        console.error(error.message)
    }

    function handleMenuClick(e: React.MouseEvent<HTMLElement>) {
        setMenuAnchorEl(e.currentTarget);
    }

    function handleMenuClose() {
        setMenuAnchorEl(null);
    }

    function handleFileChange(e: React.SyntheticEvent) {
        const target = e.target as HTMLInputElement
        if (target.files?.length) {
            formContext.setValue(props.name, target.files[0].name)
            setHasFile(true);
        } else {
            formContext.setValue(props.name, "");
            setHasFile(false);
        }
        setMenuAnchorEl(null);
    }

    async function handleOpenImage() {
        const file = fileRef.current?.files![0]!;
        const data = await file.arrayBuffer();
        openBase64InNewTab(data, file.type);
    }

    function handleClearImage() {
        fileRef.current!.value = "";
        formContext.setValue(props.name, "");
        setHasFile(false);
    }

    return (
        <Grid item xs={12} md={12} sx={{ display: "flex", alignItems: "flex-start" }}>
            <FormTextField
                name={props.name}
                label={t(`raCheckOutIn.checkDamage.addStatusPictures.fields.${props.name}`)}
                sx={{ mr: theme.spacing(1) }}
                disabled={true}
                required={props.required}
                error={!!error && t("common.error.required")}
            />

            <IconButton component="label"
                sx={{
                    ...(hasFile ? { display: "none" } : {}), // Hidden using CSS to keep input field mounted
                    alignSelf: "flex-end",
                    color: !!error ? theme.palette.error.main : theme.palette.primary.main,
                }}>
                <input hidden accept="image/*"
                    type="file"
                    onChange={handleFileChange}
                    ref={fileRef}
                />
                <UploadRounded />
            </IconButton>

            { hasFile &&
                <>
                    <IconButton
                        component="label"
                        sx={{ alignSelf: "flex-end", }}
                        onClick={handleMenuClick}
                    >
                        <GridMoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={!!menuAnchorEl}
                        onClose={handleMenuClose}
                    >
                        <MenuItem>
                            <IconButton color="primary" onClick={handleOpenImage}>
                                <VisibilityRounded />
                            </IconButton>
                        </MenuItem>
                        <MenuItem>
                            <IconButton color="primary" onClick={handleClearImage}>
                                <DeleteRounded />
                            </IconButton>
                        </MenuItem>
                    </Menu>
                </>
            }
        </Grid>
    )
}

export default PhotoFileField;
